import { useEffect, useRef, useState } from "react";
import type { LookKind, LookOptionView } from "./view-model";
import { PixelIcon } from "./PixelIcon";

export type SpotKind = "decoration" | "toy" | "companion" | "water" | "shell";

export interface SingleSpotPickerProps {
  kind: "decoration" | "toy" | "companion" | "water";
  options: LookOptionView[];
  onPick(kind: LookKind, index: number): void;
  onClose(): void;
}

export interface ShellSpotPickerProps {
  kind: "shell";
  shellColours: LookOptionView[];
  shellPatterns: LookOptionView[];
  onPick(kind: LookKind, index: number): void;
  onClose(): void;
}

export type SpotPickerProps = SingleSpotPickerProps | ShellSpotPickerProps;

const TITLES: Record<SpotKind, string> = {
  decoration: "Decoration",
  toy: "Toy",
  companion: "Companion",
  water: "Water",
  shell: "Shell",
};

/** Detect horizontal swipe over 30px and lock when vertical scroll is greater. */
export function resolveSpotSwipe(
  start: { x: number; y: number },
  end: { x: number; y: number },
): "prev" | "next" | null {
  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  if (Math.abs(deltaX) < 30 || Math.abs(deltaX) <= Math.abs(deltaY)) return null;
  return deltaX < 0 ? "next" : "prev";
}

/** Compute next or prev index with wrap around. */
export function resolveNextSpotIndex(
  currentIndex: number,
  totalCount: number,
  direction: "prev" | "next",
): number {
  if (totalCount <= 0) return 0;
  if (direction === "next") return (currentIndex + 1) % totalCount;
  return (currentIndex - 1 + totalCount) % totalCount;
}

function ShellSpotPicker(props: ShellSpotPickerProps) {
  const { shellColours, shellPatterns, onPick, onClose } = props;
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const focusedRowRef = useRef<"colour" | "pattern">("colour");

  const initialColour = Math.max(0, shellColours.findIndex((o) => o.selected));
  const [colourIdx, setColourIdx] = useState(initialColour);
  const initialPattern = Math.max(0, shellPatterns.findIndex((o) => o.selected));
  const [patternIdx, setPatternIdx] = useState(initialPattern);

  useEffect(() => {
    pickerRef.current?.focus();
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (t) touchStartRef.current = { x: t.clientX, y: t.clientY };
  };

  const currentColour = shellColours[colourIdx] ?? shellColours[0];
  const currentPattern = shellPatterns[patternIdx] ?? shellPatterns[0];

  const goPrevColour = () => {
    const prev = resolveNextSpotIndex(colourIdx, shellColours.length, "prev");
    setColourIdx(prev);
    const opt = shellColours[prev];
    if (opt && !opt.lock) onPick("shellColour", opt.index);
  };

  const goNextColour = () => {
    const next = resolveNextSpotIndex(colourIdx, shellColours.length, "next");
    setColourIdx(next);
    const opt = shellColours[next];
    if (opt && !opt.lock) onPick("shellColour", opt.index);
  };

  const goPrevPattern = () => {
    const prev = resolveNextSpotIndex(patternIdx, shellPatterns.length, "prev");
    setPatternIdx(prev);
    const opt = shellPatterns[prev];
    if (opt && !opt.lock) onPick("shellPattern", opt.index);
  };

  const goNextPattern = () => {
    const next = resolveNextSpotIndex(patternIdx, shellPatterns.length, "next");
    setPatternIdx(next);
    const opt = shellPatterns[next];
    if (opt && !opt.lock) onPick("shellPattern", opt.index);
  };

  const handleColourTouchEnd = (e: React.TouchEvent) => {
    const t = e.changedTouches[0];
    if (!touchStartRef.current || !t) return;
    const swipe = resolveSpotSwipe(touchStartRef.current, { x: t.clientX, y: t.clientY });
    touchStartRef.current = null;
    if (swipe === "next") goNextColour();
    else if (swipe === "prev") goPrevColour();
  };

  const handlePatternTouchEnd = (e: React.TouchEvent) => {
    const t = e.changedTouches[0];
    if (!touchStartRef.current || !t) return;
    const swipe = resolveSpotSwipe(touchStartRef.current, { x: t.clientX, y: t.clientY });
    touchStartRef.current = null;
    if (swipe === "next") goNextPattern();
    else if (swipe === "prev") goPrevPattern();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      e.stopPropagation();
      if (focusedRowRef.current === "pattern") goPrevPattern();
      else goPrevColour();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      e.stopPropagation();
      if (focusedRowRef.current === "pattern") goNextPattern();
      else goNextColour();
    }
  };

  const isColourSelected = !currentColour?.lock && currentColour?.selected;
  const colourLockTag = currentColour?.lock
    ? currentColour.lock === "shop"
      ? "In the shop"
      : "Achievement"
    : null;

  const isPatternSelected = !currentPattern?.lock && currentPattern?.selected;
  const patternLockTag = currentPattern?.lock
    ? currentPattern.lock === "shop"
      ? "In the shop"
      : "Achievement"
    : null;

  return (
    <div
      ref={pickerRef}
      className="spot-picker spot-picker-shell"
      data-testid="spot-picker"
      role="region"
      aria-label="Shell"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      <div className="spot-shell-rows">
        <div
          className="spot-row"
          data-testid="spot-row-colour"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleColourTouchEnd}
          onFocus={() => {
            focusedRowRef.current = "colour";
          }}
        >
          <button
            type="button"
            className="spot-btn spot-prev"
            data-testid="spot-prev"
            aria-label="Previous colour"
            onClick={goPrevColour}
          >
            <PixelIcon name="prev" />
          </button>
          <div
            className="spot-info"
            onClick={() => {
              if (currentColour?.lock) onPick("shellColour", currentColour.index);
            }}
          >
            <span className="spot-row-label">Colour</span>
            <span className="spot-name" data-testid="spot-name">
              {currentColour?.name ?? ""}
            </span>
            <span className="spot-meta">
              {isColourSelected ? (
                <span className="spot-tag spot-tag-selected" aria-label="In use">
                  <PixelIcon name="check" />
                  <span className="vh">In use</span>
                </span>
              ) : colourLockTag ? (
                <span className="spot-tag spot-tag-lock">{colourLockTag}</span>
              ) : null}
              <span className="spot-count" data-testid="spot-count">
                {`${colourIdx + 1}/${shellColours.length}`}
              </span>
            </span>
          </div>
          <button
            type="button"
            className="spot-btn spot-next"
            data-testid="spot-next"
            aria-label="Next colour"
            onClick={goNextColour}
          >
            <PixelIcon name="next" />
          </button>
        </div>

        <div
          className="spot-row"
          data-testid="spot-row-pattern"
          onTouchStart={handleTouchStart}
          onTouchEnd={handlePatternTouchEnd}
          onFocus={() => {
            focusedRowRef.current = "pattern";
          }}
        >
          <button
            type="button"
            className="spot-btn spot-prev"
            data-testid="spot-prev"
            aria-label="Previous pattern"
            onClick={goPrevPattern}
          >
            <PixelIcon name="prev" />
          </button>
          <div
            className="spot-info"
            onClick={() => {
              if (currentPattern?.lock) onPick("shellPattern", currentPattern.index);
            }}
          >
            <span className="spot-row-label">Pattern</span>
            <span className="spot-name" data-testid="spot-name">
              {currentPattern?.name ?? ""}
            </span>
            <span className="spot-meta">
              {isPatternSelected ? (
                <span className="spot-tag spot-tag-selected" aria-label="In use">
                  <PixelIcon name="check" />
                  <span className="vh">In use</span>
                </span>
              ) : patternLockTag ? (
                <span className="spot-tag spot-tag-lock">{patternLockTag}</span>
              ) : null}
              <span className="spot-count" data-testid="spot-count">
                {`${patternIdx + 1}/${shellPatterns.length}`}
              </span>
            </span>
          </div>
          <button
            type="button"
            className="spot-btn spot-next"
            data-testid="spot-next"
            aria-label="Next pattern"
            onClick={goNextPattern}
          >
            <PixelIcon name="next" />
          </button>
        </div>
      </div>

      <button
        type="button"
        className="spot-btn spot-close"
        data-testid="spot-close"
        aria-label="Close"
        onClick={onClose}
      >
        <PixelIcon name="close" />
      </button>
    </div>
  );
}

function SingleSpotPicker(props: SingleSpotPickerProps) {
  const { kind, options, onPick, onClose } = props;
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const lookKind: LookKind = kind === "water" ? "background" : kind;
  const initialIndex = Math.max(0, options.findIndex((o) => o.selected));
  const [viewIndex, setViewIndex] = useState(initialIndex);

  useEffect(() => {
    pickerRef.current?.focus();
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (t) touchStartRef.current = { x: t.clientX, y: t.clientY };
  };

  const current = options[viewIndex] ?? options[0];

  const goPrev = () => {
    const prev = resolveNextSpotIndex(viewIndex, options.length, "prev");
    setViewIndex(prev);
    const opt = options[prev];
    if (opt && !opt.lock) onPick(lookKind, opt.index);
  };

  const goNext = () => {
    const next = resolveNextSpotIndex(viewIndex, options.length, "next");
    setViewIndex(next);
    const opt = options[next];
    if (opt && !opt.lock) onPick(lookKind, opt.index);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const t = e.changedTouches[0];
    if (!touchStartRef.current || !t) return;
    const swipe = resolveSpotSwipe(touchStartRef.current, { x: t.clientX, y: t.clientY });
    touchStartRef.current = null;
    if (swipe === "next") goNext();
    else if (swipe === "prev") goPrev();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      e.stopPropagation();
      goPrev();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      e.stopPropagation();
      goNext();
    }
  };

  const isSelected = !current?.lock && current?.selected;
  const lockTag = current?.lock
    ? current.lock === "shop"
      ? "In the shop"
      : "Achievement"
    : null;

  return (
    <div
      ref={pickerRef}
      className={`spot-picker spot-picker-${kind}`}
      data-testid="spot-picker"
      role="region"
      aria-label={TITLES[kind]}
      tabIndex={-1}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        className="spot-btn spot-prev"
        data-testid="spot-prev"
        aria-label="Previous"
        onClick={goPrev}
      >
        <PixelIcon name="prev" />
      </button>

      <div
        className="spot-info"
        onClick={() => {
          if (current?.lock) onPick(lookKind, current.index);
        }}
      >
        <span className="spot-name" data-testid="spot-name">
          {current?.name ?? ""}
        </span>
        <span className="spot-meta">
          <span className="spot-count" data-testid="spot-count">
            {`${viewIndex + 1}/${options.length}`}
          </span>
          {isSelected ? (
            <span className="spot-tag spot-tag-selected" aria-label="In use">
              <PixelIcon name="check" />
              <span className="vh">In use</span>
            </span>
          ) : lockTag ? (
            <span className="spot-tag spot-tag-lock">{lockTag}</span>
          ) : null}
        </span>
      </div>

      <button
        type="button"
        className="spot-btn spot-next"
        data-testid="spot-next"
        aria-label="Next"
        onClick={goNext}
      >
        <PixelIcon name="next" />
      </button>

      <button
        type="button"
        className="spot-btn spot-close"
        data-testid="spot-close"
        aria-label="Close"
        onClick={onClose}
      >
        <PixelIcon name="close" />
      </button>
    </div>
  );
}

export function SpotPicker(props: SpotPickerProps) {
  if (props.kind === "shell") {
    return <ShellSpotPicker {...props} />;
  }
  return <SingleSpotPicker {...props} />;
}
